import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import { getHTMLFromFragment, Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import StarterKit from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import { Link } from "@tiptap/extension-link";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCaret from "@tiptap/extension-collaboration-caret";
// NOTE: must come from @tiptap/y-tiptap (TipTap's y-prosemirror fork — the
// module the Collaboration extension actually uses). Importing y-prosemirror's
// ySyncPluginKey compiles but is a DIFFERENT PluginKey instance, so getMeta
// silently never matches.
import { ySyncPluginKey } from "@tiptap/y-tiptap";
import type { PageCollabProviderLike, PageCollabUser } from "../DocumentView/PageCollabContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBold } from "@fortawesome/pro-regular-svg-icons/faBold";
import { faItalic } from "@fortawesome/pro-regular-svg-icons/faItalic";
import { faUnderline } from "@fortawesome/pro-regular-svg-icons/faUnderline";
import { faStrikethrough } from "@fortawesome/pro-regular-svg-icons/faStrikethrough";
import { faCode } from "@fortawesome/pro-regular-svg-icons/faCode";
import { faLink } from "@fortawesome/pro-regular-svg-icons/faLink";
import { faLinkSlash } from "@fortawesome/pro-regular-svg-icons/faLinkSlash";

import type {
  SlashCommandType,
  ArrowDirection,
  AutoFocusCoords,
} from "../DocumentView/DocumentContext";
import SlashCommandMenu from "./SlashCommandMenu";
import { useSlashMenu } from "./useSlashMenu";
import { cn } from "../lib/utils";
import {
  parsePastedHtml,
  decodePageBlocks,
  EACO_PAGE_BLOCKS_MIME,
  type PastedBlock,
} from "./pasteBlocks";
import { isEmptyRichTextHtml, type DocumentElement } from "@eaco/forms-core";

/**
 * The shared rich-text editor for page-document text components (PageText and
 * any future text block — a checkbox label, a callout, …). A single TipTap
 * config + floating selection toolbar so every text surface behaves the same.
 *
 * Pure and props-driven — it holds no document context. The host block wires
 * its content, editability, and Notion-style Enter/Backspace/autofocus.
 *
 * Supports: bold, italic, underline, strikethrough, inline code, links and
 * text colour. (Mentions `@` and slash `/` commands are a planned follow-on;
 * the config is structured to add them as extensions.)
 */

export interface PageRichTextProps {
  /** HTML content. */
  value: string;
  editable: boolean;
  /** Content changed. `plainText` is provided for hosts that mirror a plain
   *  string (headings' `data.text`) instead of HTML. */
  onChange: (html: string, plainText?: string) => void;
  className?: string;
  /** When set, focus at the given caret; call onAutoFocused after. */
  autoFocus?: {
    caret: number | "end";
    /** Coordinate placement (arrow up/down); takes precedence over `caret`. */
    coords?: AutoFocusCoords | null;
  } | null;
  onAutoFocused?: () => void;
  /** Enter (not Shift) — the host inserts the next block. `atStart` = caret at
   * the very start of a non-empty block (host inserts an empty text block
   * above; nothing is split). `afterHtml` carries
   *  the content that was after the caret (moved into the new block). */
  onEnter?: (isEmpty: boolean, afterHtml?: string, atStart?: boolean) => void;
  /**
   * An image file was pasted. The host uploads it and restructures the
   * document. `isEmpty` = the block had no text; `afterHtml` = content that was
   * after the caret (already removed from this block, to be re-added after the
   * image). When unset, image pastes fall through to TipTap's default handling.
   */
  onPasteImage?: (
    file: File,
    opts: { isEmpty: boolean; afterHtml?: string },
  ) => void;
  /**
   * Multi-block rich text was pasted. The host turns the parsed descriptors
   * into real page blocks (paragraphs → text blocks, list items → list
   * blocks, headings/quotes/code → their block types). When unset, rich
   * pastes coerce inline into this block as before.
   */
  onPasteBlocks?: (
    blocks: PastedBlock[],
    opts: { isEmpty: boolean; afterHtml?: string },
  ) => void;
  /**
   * Full page blocks (the private clipboard MIME) were pasted: lossless
   * intra-app paste. Checked BEFORE the HTML paste path. The host clones the
   * elements with fresh ids and inserts them after this block.
   */
  onPasteElements?: (
    elements: DocumentElement[],
    opts: { isEmpty: boolean; afterHtml?: string },
  ) => void;
  /**
   * Shift+ArrowUp/Down would extend the selection past this block's edge —
   * the host enters multi-block selection starting at this block.
   */
  onShiftArrow?: (direction: "up" | "down") => void;
  /** Backspace at the very start — the host merges into the previous block. */
  onBackspaceAtStart?: (content: { plainText: string; html: string }) => void;
  /**
   * `/` slash-command menu. When the block text starts with `/`, a menu of
   * these types shows; picking one calls `onConvert` (and the host clears the
   * `/query` by resetting the block). Both must be set to enable the menu.
   */
  slashTypes?: SlashCommandType[];
  onConvert?: (targetTypeId: string) => void;
  /** A caret arrow ran off the block edge — the host moves to the neighbour. */
  onArrow?: (direction: ArrowDirection, caretX?: number) => void;
  /** Tab / Shift-Tab — the host indents (+1) or un-indents (-1) the block.
   *  `caret` is the collapsed selection offset for post-reproject refocus. */
  onIndent?: (delta: 1 | -1, caret: number) => void;
  /**
   * Extra TipTap extensions injected by the host (e.g. the app-specific `@`
   * entity-mention node). Kept generic so forms-ui-web stays app-agnostic.
   */
  extraExtensions?: unknown[];
  /**
   * A shared flag the injected suggestion (e.g. `@` mention popup) sets while
   * open. When true, this editor yields ALL key handling (Enter/arrows/etc.) so
   * the popup can drive them. Mirrors the chat editor's mention-active ref.
   */
  suggestionActiveRef?: React.MutableRefObject<boolean>;
  /**
   * Live-collaboration binding. When set, this editor is a shared Yjs surface:
   * its content is the given `Y.XmlFragment`, with remote cursors via the
   * provider's awareness. `value` is used only to seed the fragment once, on
   * first sync of a never-collaborated block. When unset, the editor is the
   * plain HTML-prop-driven surface.
   */
  collab?: {
    fragment: import("yjs").XmlFragment;
    provider: PageCollabProviderLike;
    user: PageCollabUser;
    /** Focus/blur of this shared surface — the host broadcasts presence. */
    onFocusChange?: (focused: boolean) => void;
  } | null;
  /**
   * Plain surface (headings): no inline marks, no selection toolbar, no
   * host-injected extensions, and the inner editor carries no typography of
   * its own — it inherits from `className` on the EditorContent wrapper, so
   * e.g. a heading level change restyles without recreating the editor.
   */
  plain?: boolean;
  /** Single line: no hard breaks; pastes flatten to unformatted text. */
  singleLine?: boolean;
  /**
   * Host handle registration for cross-block text selection: called with the
   * live editor once created (and with null on destroy) so the host can
   * hit-test drags (posAtCoords) and slice/merge content across blocks.
   */
  onEditorHandle?: (editor: unknown | null) => void;
  /**
   * Cross-block VIRTUAL selection painting: the part of this block's doc that
   * belongs to the host-tracked selection, rendered as selection-look inline
   * decorations ('start'/'end' = the doc edges). The real DOM selection stays
   * collapsed while this is active.
   */
  virtualSelection?: { from: number | "start"; to: number | "end" } | null;
  /**
   * Reports empty-state transitions (including from remote collaborators'
   * edits) so the host can render its own placeholder. Deliberately a callback
   * — no per-keystroke state lives in this component.
   */
  onEmptyChange?: (empty: boolean) => void;
}

// Body copy per the Modern Editor tokens: 16px on a warm near-black ink with
// generous 1.68 line-height; inline code is a warm chip; links use the accent.
const PROSE = cn(
  "outline-none font-jakarta text-[16px] leading-[1.68] text-[#41403c]",
  // Keep spacing between paragraphs, but zero the outer margins so the first
  // line aligns with the block's gutter handle (not pushed down).
  "[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0",
  "[&_strong]:font-bold [&_em]:italic [&_u]:underline",
  "[&_s]:line-through [&_a]:underline [&_a]:text-[#2f6fdb]",
  "[&_code]:font-jbmono [&_code]:text-[13.5px] [&_code]:bg-[#f1efec] [&_code]:text-[#c0392b] [&_code]:rounded-[5px] [&_code]:px-[5px] [&_code]:py-[1.5px]",
  "[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5",
);

// Fragments this client has already seeded from HTML — prevents a re-seed if the
// same fragment's editor remounts within the session.
const SEEDED_FRAGMENTS = new WeakSet<object>();

// CollaborationCaret renders bare `.collaboration-caret__*` elements and leaves
// styling to the host — unstyled, the name label is a block div that splits the
// line it sits on into two. Inject the styles once, on the first collab editor:
// a thin steady (non-blinking) caret in the collaborator's colour with a small
// name flag floating above it (flag-in per the Eaco Editor design).
let caretCssInjected = false;
function ensureCaretCss() {
  if (caretCssInjected || typeof document === "undefined") return;
  caretCssInjected = true;
  const style = document.createElement("style");
  style.setAttribute("data-eaco-collab-caret", "");
  style.textContent = `
@keyframes eaco-flag-in{0%{opacity:0;transform:translateY(-4px);}100%{opacity:1;transform:translateY(0);}}
.collaboration-carets__caret,.collaboration-caret__caret{
  position:relative;display:inline;border-left:1.5px solid;border-right:1.5px solid;
  margin-left:-1.5px;margin-right:-1.5px;word-break:normal;pointer-events:none;
}
.collaboration-carets__label,.collaboration-caret__label{
  position:absolute;top:-1.55em;left:-1.5px;display:inline-block;
  font-size:11px;font-weight:700;line-height:1;color:#fff;
  padding:2px 5px;border-radius:4px 4px 4px 0;white-space:nowrap;
  user-select:none;pointer-events:none;font-family:inherit;
  animation:eaco-flag-in .2s ease both;
}
`;
  document.head.appendChild(style);
}

// Selection-look tint for the cross-block virtual selection — approximates the
// native ::selection highlight (the app's selection blue at ~30%). Injected
// once, on first use (decoration spans are plain class-carrying <span>s).
let vselCssInjected = false;
function ensureVSelCss() {
  if (vselCssInjected || typeof document === "undefined") return;
  vselCssInjected = true;
  const style = document.createElement("style");
  style.setAttribute("data-eaco-vsel", "");
  style.textContent = `.eaco-vsel{background:rgba(43,122,234,.30);border-radius:1px;}`;
  document.head.appendChild(style);
}

// Transaction meta used solely to poke the virtual-selection plugin into
// re-reading its host-fed range.
const VSEL_REFRESH_META = "eacoVSelRefresh";

const COLOURS = [
  { label: "Default", value: null },
  { label: "Red", value: "#e5484d" },
  { label: "Orange", value: "#f76b15" },
  { label: "Green", value: "#30a46c" },
  { label: "Blue", value: "#2b7aea" },
  { label: "Purple", value: "#8e4ec6" },
];

const ToolbarButton: React.FC<{
  active?: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}> = ({ active, onClick, label, children }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    // Use onMouseDown + preventDefault so clicking the toolbar doesn't blur the
    // editor (which would collapse the selection before the command runs).
    onMouseDown={(e) => {
      e.preventDefault();
      onClick();
    }}
    className={cn(
      "flex h-7 w-7 items-center justify-center rounded text-[13px] transition-colors",
      active
        ? "bg-[#2B7AEA]/15 text-[#2B7AEA]"
        : "text-[#dfe6f1] hover:bg-white/10 hover:text-white",
    )}
  >
    {children}
  </button>
);

const SelectionToolbar: React.FC<{ editor: Editor }> = ({ editor }) => {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkValue, setLinkValue] = useState("");
  // Re-render on editor transactions so active states + position stay current.
  const [, force] = useState(0);
  // While the link URL input is open, the WINDOW selection lives inside that
  // input (a collapsed 0×0 range) — repositioning from it would hide the
  // toolbar. In collab mode a transaction always fires right after the input
  // steals focus (the blur broadcasts an awareness/caret update), which is
  // exactly what killed "Add link" on live-collab documents; keep the toolbar
  // frozen in place until the input closes.
  const linkOpenRef = useRef(linkOpen);
  linkOpenRef.current = linkOpen;

  useEffect(() => {
    const update = () => {
      force((n) => n + 1);
      if (linkOpenRef.current) return;
      const { empty } = editor.state.selection;
      const sel = window.getSelection();
      if (!editor.isEditable || empty || !sel || sel.rangeCount === 0) {
        setPos(null);
        setLinkOpen(false);
        return;
      }
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        setPos(null);
        return;
      }
      setPos({ top: rect.top - 44, left: rect.left + rect.width / 2 });
    };
    editor.on("selectionUpdate", update);
    editor.on("transaction", update);
    return () => {
      editor.off("selectionUpdate", update);
      editor.off("transaction", update);
    };
  }, [editor]);

  if (!pos) return null;

  const applyLink = () => {
    const href = linkValue.trim();
    if (href) {
      editor.chain().focus().setLink({ href }).run();
    } else {
      editor.chain().focus().unsetLink().run();
    }
    setLinkOpen(false);
    setLinkValue("");
  };

  return createPortal(
    <div
      style={{ top: pos.top, left: pos.left }}
      className="fixed z-[9999] -translate-x-1/2"
      // Keep the editor selection alive while interacting with the toolbar.
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="flex items-center gap-0.5 rounded-lg bg-[#1f2532] px-1 py-1 shadow-lg">
        <ToolbarButton
          label="Bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <FontAwesomeIcon icon={faBold} />
        </ToolbarButton>
        <ToolbarButton
          label="Italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <FontAwesomeIcon icon={faItalic} />
        </ToolbarButton>
        <ToolbarButton
          label="Underline"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <FontAwesomeIcon icon={faUnderline} />
        </ToolbarButton>
        <ToolbarButton
          label="Strikethrough"
          active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <FontAwesomeIcon icon={faStrikethrough} />
        </ToolbarButton>
        <ToolbarButton
          label="Code"
          active={editor.isActive("code")}
          onClick={() => editor.chain().focus().toggleCode().run()}
        >
          <FontAwesomeIcon icon={faCode} />
        </ToolbarButton>

        <span className="mx-1 h-5 w-px bg-white/15" />

        {COLOURS.map((c) => (
          <button
            key={c.label}
            type="button"
            aria-label={`Colour ${c.label}`}
            title={c.label}
            onMouseDown={(e) => {
              e.preventDefault();
              if (c.value) editor.chain().focus().setColor(c.value).run();
              else editor.chain().focus().unsetColor().run();
            }}
            className="flex h-6 w-6 items-center justify-center rounded hover:bg-white/10"
          >
            <span
              className="h-3.5 w-3.5 rounded-full border border-white/25"
              style={{ backgroundColor: c.value ?? "transparent" }}
            />
          </button>
        ))}

        <span className="mx-1 h-5 w-px bg-white/15" />

        <ToolbarButton
          label={editor.isActive("link") ? "Remove link" : "Add link"}
          active={editor.isActive("link")}
          onClick={() => {
            if (editor.isActive("link")) {
              editor.chain().focus().unsetLink().run();
              return;
            }
            setLinkValue(
              (editor.getAttributes("link").href as string | undefined) ?? "",
            );
            setLinkOpen((o) => !o);
          }}
        >
          <FontAwesomeIcon icon={editor.isActive("link") ? faLinkSlash : faLink} />
        </ToolbarButton>
      </div>

      {linkOpen && (
        <div className="mt-1 flex items-center gap-1 rounded-lg bg-[#1f2532] px-2 py-1 shadow-lg">
          <input
            autoFocus
            value={linkValue}
            onChange={(e) => setLinkValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyLink();
              }
              if (e.key === "Escape") setLinkOpen(false);
            }}
            placeholder="Paste a link…"
            className="w-48 bg-transparent text-[12px] text-white placeholder:text-white/40 outline-none"
          />
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              applyLink();
            }}
            className="rounded bg-[#2B7AEA] px-2 py-0.5 text-[11px] font-semibold text-white"
          >
            Apply
          </button>
        </div>
      )}
    </div>,
    document.body,
  );
};

const PageRichText: React.FC<PageRichTextProps> = ({
  value,
  editable,
  onChange,
  className,
  autoFocus,
  onAutoFocused,
  onEnter,
  onPasteImage,
  onPasteBlocks,
  onPasteElements,
  onShiftArrow,
  onBackspaceAtStart,
  slashTypes,
  onConvert,
  onArrow,
  onIndent,
  extraExtensions,
  suggestionActiveRef,
  collab,
  plain,
  singleLine,
  onEditorHandle,
  virtualSelection,
  onEmptyChange,
}) => {
  const suggestionActiveRefRef = useRef(suggestionActiveRef);
  suggestionActiveRefRef.current = suggestionActiveRef;
  const collabRef = useRef(collab);
  collabRef.current = collab;
  const onEmptyChangeRef = useRef(onEmptyChange);
  onEmptyChangeRef.current = onEmptyChange;
  const onChangeRef = useRef(onChange);
  // Seed hold (collab only): true while this block's fragment is empty and
  // unseeded but its stored HTML has text — the window in which the mirror
  // must not be overwritten. `seedNowRef` seeds immediately from a local
  // edit in that window. Both are owned by the seed effect below.
  const seedPendingRef = useRef(false);
  const seedNowRef = useRef<(() => void) | null>(null);
  onChangeRef.current = onChange;
  const onEnterRef = useRef(onEnter);
  onEnterRef.current = onEnter;
  const onPasteImageRef = useRef(onPasteImage);
  onPasteImageRef.current = onPasteImage;
  const onPasteBlocksRef = useRef(onPasteBlocks);
  onPasteBlocksRef.current = onPasteBlocks;
  const onPasteElementsRef = useRef(onPasteElements);
  onPasteElementsRef.current = onPasteElements;
  const onShiftArrowRef = useRef(onShiftArrow);
  onShiftArrowRef.current = onShiftArrow;
  const onBackspaceRef = useRef(onBackspaceAtStart);
  onBackspaceRef.current = onBackspaceAtStart;
  const onArrowRef = useRef(onArrow);
  onArrowRef.current = onArrow;
  const onIndentRef = useRef(onIndent);
  onIndentRef.current = onIndent;
  const onEditorHandleRef = useRef(onEditorHandle);
  onEditorHandleRef.current = onEditorHandle;
  const editorRef = useRef<Editor | null>(null);

  // ---- cross-block virtual selection (host-fed) -------------------------
  // The plugin reads the CURRENT range from a ref (so the extension can be
  // created once per editor); a meta-only transaction pokes it whenever the
  // prop changes.
  const virtualSelRef = useRef(virtualSelection ?? null);
  const virtualSelectionExtension = useMemo(
    () =>
      Extension.create({
        name: "eacoVirtualSelection",
        addProseMirrorPlugins() {
          return [
            new Plugin({
              key: new PluginKey("eacoVirtualSelection"),
              props: {
                decorations(state) {
                  const v = virtualSelRef.current;
                  if (!v) return null;
                  const size = state.doc.content.size;
                  const from =
                    v.from === "start" ? 0 : Math.max(0, Math.min(v.from, size));
                  const to =
                    v.to === "end" ? size : Math.max(0, Math.min(v.to, size));
                  if (to <= from) return null;
                  return DecorationSet.create(state.doc, [
                    Decoration.inline(from, to, { class: "eaco-vsel" }),
                  ]);
                },
              },
            }),
          ];
        },
      }),
    [],
  );

  // ---- `/` slash-command menu (shared with headings etc.) ---------------
  const onConvertRef = useRef(onConvert);
  onConvertRef.current = onConvert;
  const convertFromSlash = useCallback((targetTypeId: string) => {
    // The block's text is just the "/query" at this point. The host resets
    // `data` on convert, but under live collab the text lives in the shared
    // Y.XmlFragment, which the host can't reach — clear it here (the editor
    // owns the fragment) or the query survives the conversion.
    editorRef.current?.commands.clearContent();
    onConvertRef.current?.(targetTypeId);
  }, []);
  const {
    slash,
    refresh: refreshSlashMenu,
    onKeyDown: slashKeyDown,
    select: selectSlash,
    close: closeSlash,
    isOpen: slashIsOpen,
    setIndex: setSlashIndex,
  } = useSlashMenu(slashTypes, onConvert ? convertFromSlash : undefined);
  // Recompute from the editor's text, falling back to the editor box for the
  // caret anchor.
  const refreshSlash = useCallback(() => {
    const ed = editorRef.current;
    if (!ed || !ed.isEditable) {
      closeSlash();
      return;
    }
    refreshSlashMenu(ed.getText(), () =>
      editorRef.current?.view.dom.getBoundingClientRect() ?? null,
    );
  }, [refreshSlashMenu, closeSlash]);

  const editor = useEditor({
    extensions: [
      // A page text surface is a single paragraph with inline marks. Disable
      // the block-level nodes that are separate page block types (heading,
      // lists, quote, code block, rule) so pasted content can't smuggle in
      // other text sizes / structure — it coerces to plain paragraph text,
      // keeping only inline formatting (bold/italic/underline/link/colour).
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        // Collaboration ships its own (Yjs) undo/redo — disable the default
        // history when this is a shared surface, else the two conflict.
        ...(collab ? { undoRedo: false } : {}),
        // Plain surfaces (headings) carry no inline marks; single-line ones
        // can't take soft breaks either.
        ...(plain
          ? { bold: false, italic: false, strike: false, code: false }
          : {}),
        ...(singleLine ? { hardBreak: false } : {}),
      }),
      ...(plain
        ? []
        : [
            Underline,
            Link.configure({ openOnClick: false, autolink: true }),
            TextStyle,
            Color,
          ]),
      // Live-collab: bind this editor to a per-block Y.XmlFragment + remote
      // cursors. Content then flows from Yjs, so `content` below is left empty
      // (the fragment is seeded once from `value` after first sync — see below).
      ...(collab
        ? [
            Collaboration.configure({ fragment: collab.fragment }),
            CollaborationCaret.configure({
              provider: collab.provider as never,
              user: collab.user,
            }),
          ]
        : []),
      // Host-injected app-specific extensions (e.g. the `@` entity mention).
      // Plain surfaces skip them (no mentions inside headings).
      ...((plain ? [] : extraExtensions ?? []) as never[]),
      // Cross-block virtual-selection painting (all surfaces incl. headings).
      virtualSelectionExtension,
    ],
    content: collab ? undefined : value,
    editable,
    editorProps: {
      // Plain surfaces inherit typography from the wrapper `className` instead.
      attributes: { class: plain ? "outline-none" : PROSE },
      handlePaste: (view, event) => {
        if (!view.editable) return false;
        const dt = event.clipboardData;
        if (!dt) return false;

        // Shared caret-split helper (mirrors Enter): text after the caret is
        // removed from this block and handed back to the host to re-add
        // after whatever the paste inserted.
        const splitAtCaret = (): { isEmpty: boolean; afterHtml: string } => {
          const ed = editorRef.current;
          const { selection } = view.state;
          const from = selection.from;
          const end = selection.$from.end();
          const isEmpty = view.state.doc.textContent.trim() === "";
          let afterHtml = "";
          if (ed && from < end) {
            afterHtml = getHTMLFromFragment(
              view.state.doc.slice(from, end).content as never,
              view.state.schema as never,
            );
            ed.chain().deleteRange({ from, to: end }).run();
          }
          return { isEmpty, afterHtml };
        };

        // Lossless intra-app block paste — the private MIME is checked FIRST
        // (on every surface, incl. single-line headings) so copied page
        // blocks round-trip with full fidelity instead of degrading to HTML.
        const onElements = onPasteElementsRef.current;
        if (onElements) {
          const decoded = decodePageBlocks(dt.getData(EACO_PAGE_BLOCKS_MIME));
          if (decoded) {
            event.preventDefault();
            onElements(decoded, splitAtCaret());
            return true;
          }
        }

        // Single-line surfaces (headings) accept only unformatted text, with
        // line breaks collapsed to spaces — never pasted rich text/structure.
        if (singleLine) {
          event.preventDefault();
          const text = (dt.getData("text/plain") || "").replace(
            /[\r\n]+/g,
            " ",
          );
          if (text) editorRef.current?.commands.insertContent(text);
          return true;
        }

        // Look for an image *file* in the clipboard (a pasted screenshot or a
        // copied image). Plain text/HTML pastes carry no file → fall through.
        const onPaste = onPasteImageRef.current;
        if (onPaste) {
          const fromItems = Array.from(dt.items ?? [])
            .filter((it) => it.kind === "file" && it.type.startsWith("image/"))
            .map((it) => it.getAsFile())
            .find(Boolean);
          const file =
            fromItems ??
            Array.from(dt.files ?? []).find((f) =>
              f.type.startsWith("image/"),
            ) ??
            null;
          if (file) {
            event.preventDefault();
            onPaste(file, splitAtCaret());
            return true;
          }
        }

        // Multi-block rich text → real page blocks (paragraphs, list items,
        // headings, quotes, code). Single inline fragments fall through to
        // the default inline paste.
        const onBlocks = onPasteBlocksRef.current;
        if (onBlocks) {
          const blocks = parsePastedHtml(dt.getData("text/html"));
          if (blocks) {
            event.preventDefault();
            onBlocks(blocks, splitAtCaret());
            return true;
          }
        }
        return false;
      },
      handleKeyDown: (view, event) => {
        // A host-injected suggestion popup (e.g. `@` mention) is open — yield
        // ALL key handling to it (Enter selects, arrows navigate, Esc closes).
        if (suggestionActiveRefRef.current?.current) return false;
        // Slash menu owns navigation keys while it's open with matches.
        if (slashKeyDown(event.key)) {
          event.preventDefault();
          return true;
        }
        // Tab / Shift-Tab indents the block itself (outline depth) — the host
        // owns the depth rule (max one deeper than the block above).
        if (
          event.key === "Tab" &&
          onIndentRef.current &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.altKey
        ) {
          event.preventDefault();
          const caret = Math.max(view.state.selection.anchor - 1, 0);
          onIndentRef.current(event.shiftKey ? -1 : 1, caret);
          return true;
        }
        if (event.key === "Enter" && !event.shiftKey && onEnterRef.current) {
          event.preventDefault();
          const ed = editorRef.current;
          const { selection } = view.state;
          const from = selection.from;
          // End of the current textblock's content (not the whole doc), so the
          // delete stays inside the paragraph.
          const end = selection.$from.end();
          const isEmpty = view.state.doc.textContent.trim() === "";
          // Caret at the very start of a non-empty block (same position test as
          // Backspace below): nothing to split — the host inserts an empty text
          // block above and this block keeps its content and the caret.
          if (!isEmpty && selection.empty && selection.$from.pos === 1) {
            onEnterRef.current(false, undefined, true);
            return true;
          }
          // Split at the caret: the content after it moves into the new block,
          // and is removed from this one.
          let afterHtml = "";
          if (ed && from < end) {
            // Casts sidestep a dev-only dual prosemirror-model install (the
            // aliased source vs apps/web); it's the same library at runtime.
            afterHtml = getHTMLFromFragment(
              view.state.doc.slice(from, end).content as never,
              view.state.schema as never,
            );
            ed.chain().deleteRange({ from, to: end }).run();
          }
          onEnterRef.current(isEmpty, afterHtml);
          return true;
        }
        if (event.key === "Backspace" && onBackspaceRef.current) {
          const sel = view.state.selection;
          if (sel.empty && sel.$from.pos === 1) {
            event.preventDefault();
            onBackspaceRef.current({
              plainText: view.state.doc.textContent,
              html: editorRef.current?.getHTML() ?? "",
            });
            return true;
          }
        }
        // Shift+ArrowUp/Down at the block edge: the selection would extend
        // PAST this block — hand off to the host to enter multi-block
        // selection (Notion-style). In-block shift-selection is untouched
        // (endOfTextblock is false until the head reaches the edge).
        if (
          onShiftArrowRef.current &&
          !slashIsOpen() &&
          event.shiftKey &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.altKey &&
          (event.key === "ArrowUp" || event.key === "ArrowDown")
        ) {
          const dir = event.key === "ArrowUp" ? "up" : "down";
          if (view.endOfTextblock(dir)) {
            event.preventDefault();
            onShiftArrowRef.current(dir);
            return true;
          }
        }
        // Cross-block arrow navigation: when the caret is collapsed at the edge
        // of this text block, hand off to the neighbour (endOfTextblock tells us
        // an arrow would leave the block). Up/Down carry the caret's screen x so
        // the neighbour can match the column.
        if (
          onArrowRef.current &&
          !slashIsOpen() &&
          !event.shiftKey &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.altKey &&
          view.state.selection.empty
        ) {
          const dirMap = {
            ArrowLeft: "left",
            ArrowRight: "right",
            ArrowUp: "up",
            ArrowDown: "down",
          } as const;
          const dir = dirMap[event.key as keyof typeof dirMap];
          if (dir && view.endOfTextblock(dir)) {
            const caretX = view.coordsAtPos(view.state.selection.from).left;
            event.preventDefault();
            onArrowRef.current(dir, caretX);
            return true;
          }
        }
        return false;
      },
    },
    onUpdate: ({ editor, transaction }) => {
      // Empty-state tracks ALL changes, including remote collaborators' (a
      // host-rendered placeholder must clear when a peer types).
      onEmptyChangeRef.current?.(editor.isEmpty);
      // Skip transactions applied FROM Yjs (a collaborator's remote change):
      // only the author mirrors HTML into `data.content`. A receiver echoing
      // the mirror would double-write every remote keystroke and mis-attribute
      // the server-materialised content to the receiver. Non-collab editors
      // never carry this meta, so the check is a no-op for them.
      if (transaction.getMeta(ySyncPluginKey)) return;
      // Only a real content change is mirrored. TipTap also emits `update`
      // for editable toggles (setEditable) with an empty transaction — on a
      // freshly mounted collab block that fires BEFORE the fragment is seeded
      // from `value`, and mirroring the still-empty editor would overwrite the
      // stored HTML with "<p></p>" and leave the seed nothing to seed from.
      if (!transaction.docChanged) return;
      // Seed hold: the fragment has not received this block's text yet, so
      // the stored HTML mirror is the ONLY copy of it. Nothing this editor
      // produces in that window may overwrite it — any local change here
      // (a keystroke from a fast typist, a paste, a plugin transaction) can
      // only carry what was typed, never the stored text. Instead the edit
      // self-elects the seed so the stored text goes in first (see the seed
      // effect below); that seed's own transaction then mirrors normally.
      if (seedPendingRef.current) {
        queueMicrotask(() => seedNowRef.current?.());
        return;
      }
      onChangeRef.current(editor.getHTML(), editor.getText());
      refreshSlash();
    },
    onCreate: ({ editor }) => onEmptyChangeRef.current?.(editor.isEmpty),
    onFocus: () => collabRef.current?.onFocusChange?.(true),
    onBlur: () => {
      closeSlash();
      collabRef.current?.onFocusChange?.(false);
    },
    // Recreate the editor when the collab fragment resolves (or changes): on a
    // joiner, a block can mount before its Y.XmlFragment has synced in, so the
    // editor would otherwise be built with no Collaboration binding and stay
    // empty. Keyed on the fragment identity so it rebinds exactly once.
  }, [collab?.fragment ?? null]);
  editorRef.current = editor;

  // Remote-caret styling is global (widgets are injected by ProseMirror,
  // outside this component's DOM) — ensure it exists once collab is active.
  useEffect(() => {
    if (collab) ensureCaretCss();
  }, [collab]);

  // Cross-block selection: hand the live editor to the host registry (null on
  // teardown), so drags can hit-test this block and ops can slice its doc.
  useEffect(() => {
    if (!editor) return;
    onEditorHandleRef.current?.(editor);
    return () => onEditorHandleRef.current?.(null);
  }, [editor]);

  // Repaint the virtual-selection decorations whenever the host-fed range
  // changes (meta-only transaction; the plugin reads the ref).
  useEffect(() => {
    virtualSelRef.current = virtualSelection ?? null;
    if (virtualSelection) ensureVSelCss();
    const ed = editorRef.current;
    if (ed && !ed.isDestroyed) {
      ed.view.dispatch(ed.state.tr.setMeta(VSEL_REFRESH_META, true));
    }
  }, [virtualSelection]);

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  // Sync external content in when not actively editing (avoids caret reset).
  // Skipped in collab mode — Yjs is the source of truth there, and pushing the
  // HTML prop back in would fight the CRDT.
  useEffect(() => {
    if (!editor || editor.isFocused || collab) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor, value, collab]);

  // Collab seed: a never-collaborated block starts with an empty fragment, so
  // the first client to open it populates the fragment from its stored HTML.
  // Do it once, after the provider's first sync (so a late joiner whose fragment
  // already has content never re-seeds), guarded per-fragment so this client
  // can't double-seed.
  const seedValueRef = useRef(value);
  seedValueRef.current = value;
  // Keyed on the fragment + provider identities, not the `collab` object: hosts
  // build that object inline, so depending on it would restart the seed timer
  // on every render and a host that re-renders faster than the delay (the
  // assistant workspace while a reply streams) would never seed.
  const seedFragment = collab?.fragment ?? null;
  const seedProvider = collab?.provider ?? null;
  useEffect(() => {
    if (!seedFragment || !seedProvider || !editor) return;
    let cancelled = false;
    const fragment = seedFragment;
    const provider = seedProvider;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Seed hold (see onUpdate): while the fragment is empty and unseeded but
    // the stored HTML has text, that HTML is the only copy of the block's
    // content and the editor must not mirror over it. Lifted by our own seed,
    // or by a peer's seed arriving in the fragment.
    seedPendingRef.current =
      fragment.length === 0 &&
      !SEEDED_FRAGMENTS.has(fragment) &&
      !isEmptyRichTextHtml(seedValueRef.current);
    const onFragment = (_event: unknown, tr: { local: boolean }) => {
      if (!tr.local && fragment.length > 0) seedPendingRef.current = false;
    };
    fragment.observe(onFragment);
    const seed = (html: string) => {
      // The self-elected seed runs from a microtask, which can outlive a
      // fast unmount; leave the fragment for the next editor to seed.
      if (editor.isDestroyed) return;
      SEEDED_FRAGMENTS.add(fragment);
      seedPendingRef.current = false;
      editor.commands.setContent(html, { emitUpdate: true });
    };
    const attempt = () => {
      if (cancelled) return;
      const html = seedValueRef.current;
      // Nothing to seed: a peer already did (fragment has content), this
      // client already did, or the stored HTML is genuinely empty — TipTap's
      // "<p></p>" included, which must never be seeded as if it were text.
      if (
        fragment.length !== 0 ||
        SEEDED_FRAGMENTS.has(fragment) ||
        isEmptyRichTextHtml(html)
      ) {
        seedPendingRef.current = false;
        return;
      }
      // When several clients open a never-collaborated doc at once, ALL of
      // them see an empty fragment after sync — so elect exactly one seeder:
      // the lowest awareness clientID inserts the mirror content, everyone
      // else waits for that seed to arrive (re-checking in case the elected
      // client leaves before seeding).
      const { awareness } = provider as {
        awareness: { clientID: number; getStates: () => Map<number, unknown> };
      };
      const ids = [...awareness.getStates().keys()];
      const lowest = ids.length ? Math.min(...ids) : awareness.clientID;
      if (lowest < awareness.clientID) {
        timer = setTimeout(attempt, 2500);
        return;
      }
      seed(html);
    };
    // Self-election: a local edit landed while the hold was on. Seed the
    // stored text right now (the edit itself is replaced — it could only be
    // a keystroke or two), skipping the election: waiting would let the
    // elected peer's seed overwrite the text this user keeps typing. Never
    // before the first sync, though — an empty fragment then may simply not
    // have arrived yet, and seeding over it would double the text when it
    // does (the double-seed corruption). Such an edit defers to the sync.
    let synced = false;
    let seedAfterSync = false;
    seedNowRef.current = () => {
      if (cancelled || !seedPendingRef.current) return;
      if (!synced) {
        seedAfterSync = true;
        return;
      }
      if (timer) clearTimeout(timer);
      const html = seedValueRef.current;
      if (SEEDED_FRAGMENTS.has(fragment) || isEmptyRichTextHtml(html)) {
        seedPendingRef.current = false;
        return;
      }
      seed(html);
    };
    provider.whenSynced.then(() => {
      if (cancelled) return;
      synced = true;
      // A pre-sync local edit is waiting: if the sync did not bring the
      // fragment's content from a peer (the hold is still on), this client
      // is the writer — seed now rather than electing.
      if (seedAfterSync && seedPendingRef.current) {
        seedNowRef.current?.();
        return;
      }
      // Let peer awareness arrive first (peers re-announce within ~300ms of
      // seeing our sync Step1) so the election sees everyone already here.
      timer = setTimeout(attempt, 800);
    });
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      fragment.unobserve(onFragment);
      seedPendingRef.current = false;
      seedNowRef.current = null;
    };
  }, [seedFragment, seedProvider, editor]);

  // Autofocus at a requested caret offset, on the next frame so any external
  // content sync (e.g. a merge) has applied first.
  const consumeRef = useRef(onAutoFocused);
  consumeRef.current = onAutoFocused;
  const caret = autoFocus?.caret;
  const coords = autoFocus?.coords ?? null;
  const coordsX = coords?.x ?? null;
  const coordsEdge = coords?.edge ?? null;
  const shouldFocus = !!autoFocus;
  useEffect(() => {
    if (!(shouldFocus && editor)) return;
    const run = () => {
      if (coordsX !== null && coordsEdge !== null) {
        // Arrow up/down: drop the caret at the screen x on our first/last line.
        const dom = editor.view.dom as HTMLElement;
        const rect = dom.getBoundingClientRect();
        // A point just inside the first/last line, at the requested column.
        const y =
          coordsEdge === "first" ? rect.top + 8 : rect.bottom - 8;
        const found = editor.view.posAtCoords({ left: coordsX, top: y });
        if (found) {
          editor.commands.focus(found.pos);
        } else {
          editor.commands.focus(coordsEdge === "first" ? "start" : "end");
        }
      } else if (typeof caret === "number") {
        const pos = Math.min(caret + 1, editor.state.doc.content.size);
        editor.commands.focus(pos);
      } else {
        editor.commands.focus("end");
      }
      // commands.focus() sets the selection but defers the actual DOM focus
      // into TipTap's own internal rAF (guarded on editor state) — which can
      // silently never land. We already deferred appropriately to get here;
      // focus the view synchronously so the caret is live NOW and the user's
      // next keystroke goes to this block, not the previous one.
      editor.view.focus();
      consumeRef.current?.();
    };
    // Next frame, so any external content sync (e.g. a merge) has applied
    // first. But rAF callbacks are SUSPENDED while the tab is hidden — a block
    // created on a hidden/backgrounding tab (automation/E2E; a user switching
    // tabs the same instant) would otherwise never receive focus, and the next
    // keystrokes would land in the previous block. Race a timeout fallback
    // against the rAF: whichever fires first focuses; the other is a no-op.
    let done = false;
    const runOnce = () => {
      if (done) return;
      done = true;
      run();
    };
    const raf = requestAnimationFrame(runOnce);
    const timer = setTimeout(runOnce, 150);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [shouldFocus, editor, caret, coordsX, coordsEdge]);

  return (
    <>
      <EditorContent editor={editor} className={cn(!editable && PROSE, className)} />
      {editor && editable && !plain && <SelectionToolbar editor={editor} />}
      {editor && editable && slash && (
        <SlashCommandMenu
          items={slash.items}
          activeIndex={slash.index}
          position={slash.position}
          onSelect={selectSlash}
          onHover={setSlashIndex}
        />
      )}
    </>
  );
};

export default PageRichText;
